/**
 * PublicTermsPage — 외부 공개용 약관 / 개인정보처리방침 단독 페이지.
 *
 * URL(웹 공개): /legal/privacy, /legal/terms
 *
 * 배경: SNS 로그인 심사(구글 OAuth·네이버·카카오·애플)와 Google Play 데이터 안전 양식은
 *   "외부에서 접근 가능한 개인정보처리방침 URL" 을 요구한다. prod 웹은 VITE_WEB_PUBLIC=false 라
 *   /gift/* 외 전 경로가 ComingSoon 으로 차단되므로, 웹 게이트(webGateRoutes)에 이 경로만 예외로 노출한다.
 *
 * TermsViewerPage 와의 차이: 그쪽은 앱 내 SubActivity(ActivityHost 의 AppBar / nav.close 의존)라
 *   공개 웹에 그대로 못 붙는다. 이 페이지는 데이터 훅·정적 fallback 만 재사용하고 자체 문서 레이아웃으로 렌더한다.
 *
 * 데이터 소스: BE `GET /api/v1/user/terms/{division}/latest?lang=` (useLatestTermsQuery).
 *   요청 언어의 번역본이 없으면 정본(한국어) 본문이 내려오고, 그때만 원문 안내를 띄운다.
 *
 * 언어 지정: URL 쿼리 `?lang=ko|en|ja` (예: /legal/privacy?lang=en). resolvePublicLanguage 참조.
 *   404 / BE 미가용 → 정적 fallback(termsContent.ts). 심사 시점에 BE 미가용이어도 최소 본문은 노출된다.
 */
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TERMS_CONTENT, type TermsType } from "../../components/terms/termsContent";
import { useLatestTermsQuery } from "../../hooks/useTermsQueries";
import { sanitizeHtml } from "../../lib/sanitizeHtml";
import { track } from "../../lib/tracking";
import { currentLanguage, resolvePublicLanguage } from "../../i18n";
import styles from "./PublicTermsPage.module.css";

/** TermsViewerPage 와 동일한 ?type → TERMS_DIV 공통코드 매핑. */
const TYPE_TO_DIVISION_CODE: Record<TermsType, string | null> = {
  service: "SERVICE",
  privacy: "PRIVACY",
  marketing: "MKTG",
  oss: null,
};

/* 표시 언어 결정(?lang= 우선)은 소개 페이지와 같은 규칙을 쓴다 — i18n/resolvePublicLanguage. */

interface PublicTermsPageProps {
  type: TermsType;
}

export default function PublicTermsPage({ type }: PublicTermsPageProps) {
  const fallback = TERMS_CONTENT[type];
  const divisionCode = TYPE_TO_DIVISION_CODE[type];
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const langParam = searchParams.get("lang");
  // i18n.language 를 의존성에 둬야 언어 변경 시 다시 조회된다.
  const language = useMemo(
    () => resolvePublicLanguage(langParam, currentLanguage()),
    [langParam, i18n.language],
  );
  const { data: latest, isLoading } = useLatestTermsQuery(divisionCode ?? "", language);

  useEffect(() => {
    // 기존 등록 이벤트 재사용(EVENT_PARAM_ALLOWLIST 에 terms_view:["type"] 등록됨).
    track("terms_view", { type });
  }, [type]);

  const bodyHtml = divisionCode ? latest?.bodyContent : null;
  const showLoading = divisionCode !== null && isLoading && !bodyHtml;
  const showOriginalNotice =
    language !== "ko" && divisionCode !== null && (!bodyHtml || latest?.languageCode !== language);

  return (
    <main className={styles.root}>
      <article className={styles.doc}>
        {/* 본문이 BE 에서 왔으면 그 언어의 약관명을 제목으로 쓴다 — 영문 본문 위에 한국어 제목이
            남는 것을 막는다. 정적 폴백일 때만 하드코딩 제목(한국어). */}
        <h1 className={styles.title}>{latest?.termsName ?? fallback.title}</h1>
        {showOriginalNotice && !showLoading ? (
          <p className={styles.muted}>{t("terms.originalNotice")}</p>
        ) : null}
        {showLoading ? (
          <p className={styles.muted}>{t("terms.loading")}</p>
        ) : bodyHtml ? (
          <div
            className={styles.body}
            // BE sanitize + FE DOMPurify 이중 방어.
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(bodyHtml) }}
          />
        ) : (
          <div className={styles.fallbackBody}>{fallback.body.trim()}</div>
        )}
        <footer className={styles.footer}>Peeka · kr.duckflex.peeka.app</footer>
      </article>
    </main>
  );
}
